/** Maneuver types shown in Live MAP ALL 2x2 map grid. */
export const LIVE_ALL_GRID_MANEUVER_TYPES = ['TACK', 'GYBE', 'ROUNDUP', 'BEARAWAY'] as const;

export type LiveAllGridManeuverType = (typeof LIVE_ALL_GRID_MANEUVER_TYPES)[number];

export const MAP_ALL_MANEUVER_VIEW = 'MAP ALL';

export function normalizeManeuverViewName(viewName: string): string {
  return (viewName || '').trim().replace(/\s+/g, ' ').toUpperCase();
}

export function isMapAllManeuverView(viewName: string): boolean {
  return normalizeManeuverViewName(viewName) === MAP_ALL_MANEUVER_VIEW;
}

export const LIVE_MANEUVER_SOLO_VIEWS = ['MAP', 'TABLE', 'SCATTER', 'TIME SERIES'] as const;

/** Sidebar MANEUVERS submenu order in live mode (MAP ALL first, then solo views). */
export const LIVE_MANEUVER_SUBMENU_VIEWS: readonly string[] = [
  MAP_ALL_MANEUVER_VIEW,
  ...LIVE_MANEUVER_SOLO_VIEWS,
];

type ManeuverMenuApiEntry = { object_name?: string; name?: string } | string;

/** Merge API maneuver_menus with live defaults so reload always shows live submenu views. */
export function buildLiveManeuverSubmenuEntries(apiMenus: ManeuverMenuApiEntry[]): { object_name: string }[] {
  const seen = new Set<string>();
  const result: { object_name: string }[] = [];

  const addName = (raw: string) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return;
    const key = normalizeManeuverViewName(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    if (isMapAllManeuverView(trimmed)) {
      result.push({ object_name: MAP_ALL_MANEUVER_VIEW });
      return;
    }
    const solo = normalizeLiveSoloView(trimmed);
    result.push({ object_name: solo ?? trimmed });
  };

  for (const view of LIVE_MANEUVER_SUBMENU_VIEWS) {
    addName(view);
  }
  for (const entry of apiMenus) {
    const name =
      typeof entry === 'string' ? entry : (entry.object_name || entry.name || '');
    const normalized = normalizeManeuverViewName(name);
    if (normalized === 'VIDEO') continue;
    addName(name);
  }
  return result;
}

export type LiveManeuverSoloView = (typeof LIVE_MANEUVER_SOLO_VIEWS)[number];

export function normalizeLiveSoloView(viewName: string): LiveManeuverSoloView | null {
  const normalized = normalizeManeuverViewName(viewName);
  if (normalized === MAP_ALL_MANEUVER_VIEW) return null;
  return LIVE_MANEUVER_SOLO_VIEWS.includes(normalized as LiveManeuverSoloView)
    ? (normalized as LiveManeuverSoloView)
    : null;
}

export function isLiveManeuverSoloView(viewName: string): boolean {
  return normalizeLiveSoloView(viewName) != null;
}

export function normalizeLiveManeuverEventType(eventTypeVal: string): string {
  const normalized = (eventTypeVal || '').trim().toUpperCase();
  if (normalized === 'ALL') return 'TACK';
  return normalized || 'TACK';
}

export function getManeuverRowSourceName(row: Record<string, unknown>): string {
  return String(row?.source_name ?? row?.sourceName ?? row?.Source_name ?? row?.source ?? '')
    .trim()
    .toUpperCase();
}

export function getManeuverRowEventType(row: Record<string, unknown>): string {
  return String(
    row?.live_maneuver_type ?? row?.event_type ?? row?.Event_type ?? row?.EVENT_TYPE ?? ''
  )
    .trim()
    .toUpperCase();
}

export function getManeuverRowTimeMs(row: Record<string, unknown>): number {
  const raw = row?.datetime ?? row?.Datetime ?? row?.DATETIME ?? row?.start_time ?? row?.Start_time;
  if (raw == null) return 0;
  const ms = new Date(String(raw)).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** Latest event_id per source (by Datetime, then event_id tie-break). */
export function latestEventIdsBySource(rows: Record<string, unknown>[]): Map<string, number> {
  const latest = new Map<string, { eventId: number; timeMs: number }>();
  for (const row of rows) {
    const source = getManeuverRowSourceName(row);
    const eventId = Number(row?.event_id);
    if (!source || !Number.isFinite(eventId)) continue;
    const timeMs = getManeuverRowTimeMs(row);
    const cur = latest.get(source);
    if (!cur || timeMs > cur.timeMs || (timeMs === cur.timeMs && eventId > cur.eventId)) {
      latest.set(source, { eventId, timeMs });
    }
  }
  return new Map([...latest.entries()].map(([source, val]) => [source, val.eventId]));
}

export function partitionRowsByLiveGridType(
  rows: Record<string, unknown>[]
): Record<LiveAllGridManeuverType, { eventIds: number[]; rows: Record<string, unknown>[] }> {
  const byType = Object.fromEntries(
    LIVE_ALL_GRID_MANEUVER_TYPES.map((type) => [type, { eventIds: [] as number[], rows: [] as Record<string, unknown>[] }])
  ) as Record<LiveAllGridManeuverType, { eventIds: number[]; rows: Record<string, unknown>[] }>;

  for (const row of rows) {
    const type = getManeuverRowEventType(row);
    if (!LIVE_ALL_GRID_MANEUVER_TYPES.includes(type as LiveAllGridManeuverType)) continue;
    const eventId = Number(row.event_id);
    if (!Number.isFinite(eventId)) continue;
    const bucket = byType[type as LiveAllGridManeuverType];
    bucket.eventIds.push(eventId);
    bucket.rows.push(row);
  }

  return byType;
}
